import { BusinessObjectBase } from './base';
import { userActivityPeriodSchema } from '@schemas/business-objects/user-activity-period';
import type { User } from './user';
import type { Id } from '../base-types';

export interface UserActivityPeriodData {
  readonly id: Id;
  readonly periodStart: Date;
  readonly requestCount?: number;
  readonly userId: Id;
  readonly user?: User;
}

export interface UserActivityPeriod extends Readonly<UserActivityPeriodData> {}
export class UserActivityPeriod extends BusinessObjectBase {
  static readonly entityName = 'UserActivityPeriod' as const;
  static readonly plural = 'user-activity-periods';
  static schema = userActivityPeriodSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<UserActivityPeriod[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new UserActivityPeriod(data));
  }

  static async findOne(filter?: any): Promise<UserActivityPeriod | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new UserActivityPeriod(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<UserActivityPeriod> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new UserActivityPeriod(data);
  }

  static async create(
    data: Omit<UserActivityPeriodData, 'id'>,
  ): Promise<UserActivityPeriod> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new UserActivityPeriod(result);
  }

  static async createMany(
    data: Omit<UserActivityPeriodData, 'id'>[],
  ): Promise<UserActivityPeriod[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new UserActivityPeriod(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<UserActivityPeriodData>,
  ): Promise<UserActivityPeriod> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new UserActivityPeriod(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
